import { useSyncExternalStore } from 'react';
import { msUntilUnusable, onGenesysSessionChange, usableAccessToken } from './token-store';

/**
 * One place that decides the admin must sign in again. Our backend answers a
 * dead token with 401 SESSION_EXPIRED; a Genesys SDK call rejects with status 401
 * (genesys-api reports those for every SDK call). App shows the session-expired
 * dialog while this is set.
 */

let expired = false;
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((listener) => listener());
}

export function reportSessionExpired(): void {
  if (expired) return;
  expired = true;
  console.info('[AUTH] Genesys session expired; sign-in required');
  emit();
}

export function resetSessionExpired(): void {
  if (!expired) return;
  expired = false;
  emit();
}

export function isSessionExpired(): boolean {
  return expired;
}

export function subscribeSessionExpired(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useSessionExpired(): boolean {
  return useSyncExternalStore(subscribeSessionExpired, isSessionExpired, isSessionExpired);
}

export class SessionExpiredError extends Error {
  readonly code = 'SESSION_EXPIRED';
  constructor() {
    super('Your session expired. Sign in again.');
    this.name = 'SessionExpiredError';
  }
}

export class GenesysUnavailableError extends Error {
  readonly code = 'GENESYS_UNAVAILABLE';
  constructor() {
    super('Genesys Cloud is unavailable. Try again.');
    this.name = 'GenesysUnavailableError';
  }
}

/** A Genesys SDK rejection for a dead token (the SDK rejects with the error body). */
export function isGenesysAuthFailure(error: unknown): boolean {
  return !!error && typeof error === 'object' && (error as { status?: unknown }).status === 401;
}

/**
 * fetch to our backend with the admin's token. Does not send an expired or
 * nearly expired token. A 401 reports the session expired; a 503
 * GENESYS_UNAVAILABLE becomes its own retryable error.
 */
export async function authorizedFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const token = expired ? '' : usableAccessToken();
  if (!token) {
    reportSessionExpired();
    throw new SessionExpiredError();
  }
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  if (response.status === 401) {
    reportSessionExpired();
    throw new SessionExpiredError();
  }
  if (response.status === 503) {
    const code = await response.clone().json()
      .then((body: unknown) => (body && typeof body === 'object' ? (body as { error?: unknown }).error : undefined))
      .catch(() => undefined);
    if (code === 'GENESYS_UNAVAILABLE') throw new GenesysUnavailableError();
  }
  return response;
}

/** Below this, a hidden tab is not re-checked with Genesys on return. */
export const RESUME_CHECK_AFTER_MS = 60 * 1000;

const MAX_TIMER_MS = 2 ** 31 - 1;

export interface SessionWatchOptions {
  /** Asks Genesys whether the token still works. */
  checkAlive: () => Promise<'ok' | 'expired' | 'unknown'>;
  now?: () => number;
}

/**
 * Reports the session expired when the token reaches its margin, and re-checks
 * it when a hidden tab becomes visible again (timers do not run while a laptop sleeps).
 * Returns a stop function.
 */
export function watchSession(options: SessionWatchOptions): () => void {
  const now = options.now ?? Date.now;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let hiddenAt: number | null = document.visibilityState === 'hidden' ? now() : null;
  let stopped = false;

  const check = (): boolean => {
    if (expired) return false;
    if (!usableAccessToken(now())) {
      reportSessionExpired();
      return false;
    }
    return true;
  };

  const arm = () => {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
    const ms = msUntilUnusable(now());
    if (ms === null || stopped) return;
    timer = setTimeout(() => {
      timer = undefined;
      if (check()) arm();
    }, Math.min(ms + 50, MAX_TIMER_MS));
  };

  const onVisibility = () => {
    if (document.visibilityState === 'hidden') {
      hiddenAt = now();
      return;
    }
    const hiddenFor = hiddenAt === null ? 0 : now() - hiddenAt;
    hiddenAt = null;
    if (!check()) return;
    arm();
    if (hiddenFor < RESUME_CHECK_AFTER_MS) return;
    const checkedToken = usableAccessToken(now());
    void options.checkAlive().then((result) => {
      if (!stopped && result === 'expired' && usableAccessToken(now()) === checkedToken) reportSessionExpired();
    });
  };

  const stopTokenWatch = onGenesysSessionChange(arm);
  document.addEventListener('visibilitychange', onVisibility);
  arm();

  return () => {
    stopped = true;
    if (timer !== undefined) clearTimeout(timer);
    stopTokenWatch();
    document.removeEventListener('visibilitychange', onVisibility);
  };
}
